﻿import { useState, useEffect } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { useToasts } from "react-toast-notifications";
import Select from "react-select";
import { useSelector } from "react-redux";

import { Row, Col, Form, Modal, Button } from "react-bootstrap";

import classes from "../../../maintainers.module.scss";

import { decimalValidateNew, formatMilesDecimalNew, hasPermission, integerValidate, toast, validRoles } from "@/services/Utils";
import api from "@/services/api.service";
import Fetching from "@/components/Fetching/Fetching";

import { base_business_center, customStyles } from "../business_center.constants";

import { NoOptionsMessage } from "@/components/ReactSelect/NoOptions";
import { CropInput, CustomSingleValue } from "@/components/Form/Inputs/Crop/CropInput";

const BusinessCenterForm = ({ estates_parents, estates, categories, species, varieties, quarters, crops, confirmSave, permissions }) => {
	const { addToast } = useToasts();
	const location = useLocation();
	const { option, slug } = useParams();
	const navigate = useNavigate();

	const { customer: customerConfig } = useSelector((state) => state.auth.user);

	const [isFetching, setIsFetching] = useState(true);
	const [data, setData] = useState({ ...base_business_center });
	const [localCrops, setLocalCrops] = useState([]);

	const userRoles = JSON.parse(localStorage.getItem("roles"));
	const isManager = validRoles(userRoles, ["administrador", "gerente_agricola", "administrador_campo"]);

	const handleClose = () => {
		emptyFields();
		if (location.pathname.includes("configuracion")) {
			navigate("/configuracion/centro_costo");
			return;
		}

		if (location.pathname.includes("mantenedores")) {
			navigate("/mantenedores/centro_costo");
			return;
		}

		if (location.pathname.includes("mis_campos")) {
			navigate("/mis_campos");
			return;
		}
	};

	const validSave = () => {
		if (!data.name) {
			toast(addToast, "Debe ingresar un nombre para guardar.", "error");
			return false;
		}

		if (data.estates && data.estates.length === 0) {
			toast(addToast, "Debe seleccionar al menos un campo para guardar.", "error");
			return false;
		}

		if (!data.customer_id && location.pathname.includes("mantenedores")) {
			addToast("Debe seleccionar un cliente para guardar.", {
				appearance: "error",
			});

			return false;
		}

		return true;
	};

	const handleSave = () => {
		if (validSave()) {
			confirmSave({ ...data }, setIsFetching, afterSave);
		}
	};

	const emptyFields = () => {
		setData({ ...base_business_center });
	};

	const afterSave = () => {
		handleClose();
		addToast("Centro de costo guardado correctamente.", {
			appearance: "success",
		});
	};

	useEffect(() => {
		setIsFetching(true);
		if (option && slug && ["crear", "editar"].includes(option)) {
			api.get("/admin/business_center/detail", { slug, option }).then((response) => {
				const { detail } = response;
				if (detail) {
					setData({ ...detail });
				} else {
					addToast("Ocurrió un problema al cargar el centro de costo seleccionado.", {
						appearance: "error",
					});
					handleClose();
				}
				setIsFetching(false);
			});
		} else {
			let newData = { ...base_business_center };
			if (estates_parents.length === 1) {
				newData.estate_parent_id = estates_parents[0].value;
			}
			if (estates.length === 1) {
				newData.estate_id = estates[0].value;
				newData.estates = [estates[0].value];
			}

			setData(newData);

			setIsFetching(false);
		}
		// eslint-disable-next-line
	}, [slug, option]);

	useEffect(() => {
		if (localCrops.length === 0) {
			setLocalCrops(crops);
		}
		// eslint-disable-next-line
	}, [crops]);

	const getSpecies = (selected_estates) => {
		let new_species = [];

		species.forEach((s) => {
			s.estates.forEach((es) => {
				if (selected_estates.includes(es)) {
					if (!new_species.find((ns) => Number(ns.value) === Number(s.value))) {
						if (Number(s.category_id) === Number(data.category_id)) {
							new_species.push(s);
							return;
						}
					}
				}
			});
		});

		return new_species;
	};

	const handleEstate = async (new_estates) => {
		const species_available = getSpecies(new_estates);

		let new_species = [];
		data.species &&
			data.species.forEach((s) => {
				if (species_available.find((sa) => Number(sa.value) === Number(s))) {
					new_species.push(s);
				}
			});

		setData({
			...data,
			species: new_species,
			estates: new_estates,
			estate_id: new_estates.length !== 0 ? new_estates[0] : "",
		});
	};

	const typeBusinessCenter = [
		{ value: "1", label: "OPEX" },
		{ value: "2", label: "CAPEX" },
	];

	return (
		<Modal
			show={option && ["crear", "editar"].includes(option) ? true : false}
			onHide={handleClose}
			backdrop="static"
			keyboard={false}
			className={classes.main_form}
			size="lg"
		>
			<Modal.Header closeButton>
				<h4 className={classes.modal_title}>Centro de Costo</h4>
			</Modal.Header>
			<Modal.Body>
				{isFetching ? (
					<Fetching topMargin={"1em"} />
				) : (
					<Row className={classes.row_form}>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Nombre</Form.Label>

								<Form.Control
									value={data.name}
									onChange={(e) =>
										setData({
											...data,
											name: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Superficie Hectáreas</Form.Label>
								<Form.Control
									value={data.area ? formatMilesDecimalNew(data.area) : ""}
									onChange={(e) => {
										let newValue = String(e.currentTarget.value).replaceAll(".", "");
										setData({
											...data,
											area: newValue,
										});
									}}
									type="text"
									style={{ textAlign: "right" }}
									onKeyDown={decimalValidateNew}
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Especie</Form.Label>
								<Select
									value={localCrops && localCrops.find((crop) => String(crop.value) === String(data.specie))}
									placeholder={<div>Seleccione</div>}
									options={localCrops ? localCrops : []}
									styles={customStyles}
									isDisabled={!isManager}
									onChange={(value) => {
										setData({
											...data,
											specie: value ? value.value : "",
											variety: "",
											crop_detail_id: "",
										});
									}}
									components={{ Option: CropInput, SingleValue: CustomSingleValue }}
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Variedad</Form.Label>
								<Select
									value={
										data.specie && data.crop_detail_id
											? localCrops
													.find((crop) => String(crop.value) === String(data.specie))
													?.varieties?.find((v) => String(v.value) === String(data.crop_detail_id)) || null
											: null
									}
									placeholder={<div>Seleccione</div>}
									options={data.specie ? localCrops.find((crop) => String(crop.value) === String(data.specie))?.varieties || [] : []}
									styles={customStyles}
									isDisabled={!isManager || !data.specie}
									onChange={(value) => {
										setData({
											...data,
											variety: value ? value.label : "",
											crop_detail_id: value ? value.value : "",
										});
									}}
									components={{ NoOptionsMessage }}
									isClearable
								/>
							</Form.Group>
						</Col>{" "}
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Año de Plantación</Form.Label>
								<Form.Control
									value={data.year ? data.year : ""}
									onChange={(e) =>
										setData({
											...data,
											year: e.currentTarget.value,
										})
									}
									type="text"
									onKeyDown={integerValidate}
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Sistema de Conducción</Form.Label>
								<Form.Control
									value={data.conduction_system ? data.conduction_system : ""}
									onChange={(e) =>
										setData({
											...data,
											conduction_system: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Tipo de Riego</Form.Label>
								<Form.Control
									value={data.type_irrigation ? data.type_irrigation : ""}
									onChange={(e) =>
										setData({
											...data,
											type_irrigation: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Portainjerto</Form.Label>
								<Form.Control
									value={data.rootstock ? data.rootstock : ""}
									onChange={(e) =>
										setData({
											...data,
											rootstock: e.currentTarget.value,
										})
									}
									type="text"
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Distancia de Plantación (metros)</Form.Label>

								<Row>
									<Col md={5} xs={5}>
										<Form.Control
											value={data.plantation_distance ? formatMilesDecimalNew(data.plantation_distance) : ""}
											onChange={(e) => {
												let newValue = String(e.currentTarget.value).replaceAll(".", "");

												setData({
													...data,
													plantation_distance: newValue,
												});
											}}
											type="text"
											style={{ textAlign: "right" }}
											onKeyDown={decimalValidateNew}
										/>
									</Col>
									<Col
										md={2}
										xs={2}
										style={{
											display: "flex",
											justifyContent: "center",
											alignItems: "center",
										}}
									>
										X
									</Col>
									<Col md={5} xs={5}>
										<Form.Control
											value={data.plantation_distance_end ? formatMilesDecimalNew(data.plantation_distance_end) : ""}
											onChange={(e) => {
												let newValue = String(e.currentTarget.value).replaceAll(".", "");

												setData({
													...data,
													plantation_distance_end: newValue,
												});
											}}
											type="text"
											style={{ textAlign: "right" }}
											onKeyDown={decimalValidateNew}
										/>
									</Col>
								</Row>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Fecha Probable de Cosecha</Form.Label>
								<Form.Control
									type="date"
									className={`form-control`}
									placeholder="Fecha de cosecha"
									value={data.harvest_date ?? ""}
									onChange={(e) => setData({ ...data, harvest_date: e.target.value })}
								/>
							</Form.Group>
						</Col>
						{customerConfig && customerConfig.id === 47 && (
							<Col md={6} xs={12}>
								<Form.Group className={classes.group_form}>
									<Form.Label className={classes.label}>Tipo de Centro de Costo</Form.Label>

									<Select
										value={typeBusinessCenter.filter((c) => Number(c.value) === Number(data.business_center_type_id))}
										placeholder={<div>Seleccione</div>}
										options={typeBusinessCenter}
										onChange={(value) => {
											setData({
												...data,
												business_center_type_id: value ? value.value : "",
											});
										}}
										components={{
											NoOptionsMessage,
										}}
									/>
								</Form.Group>
							</Col>
						)}
						{!customerConfig.use_sectors && (
							<>
								{!!customerConfig.use_estates_parents && (
									<Col md={6} xs={12}>
										<Form.Group className={classes.group_form}>
											<Form.Label className={classes.label}>
												{customerConfig.customer_screens.find((cs) => cs.screen_id === 38)
													? customerConfig.customer_screens.find((cs) => cs.screen_id === 38)?.name
													: "Grupos Campos"}
											</Form.Label>

											<Select
												value={estates_parents && estates_parents.filter((c) => Number(c.value) === Number(data.estate_parent_id))}
												placeholder={<div>Seleccione</div>}
												options={estates_parents ? estates_parents : []}
												onChange={(value) => {
													const availableEstates = value
														? estates.filter((e) => Number(e.estate_parent_id) === Number(value.value)).map((e) => e.value)
														: [];

													setData({
														...data,
														estate_parent_id: value ? value.value : "",
														estate_id: "",
														estates: availableEstates,
													});
												}}
												components={{
													NoOptionsMessage,
												}}
												isClearable
											/>
										</Form.Group>
									</Col>
								)}

								<Col md={6} xs={12}>
									<Form.Group className={classes.group_form}>
										<Form.Label className={classes.label}>
											{customerConfig.customer_screens.find((cs) => cs.screen_id === 51)
												? customerConfig.customer_screens.find((cs) => cs.screen_id === 51)?.name
												: "Campos"}
										</Form.Label>
										<Select
											value={estates && estates.filter((et) => data.estates.includes(et.value))}
											placeholder={<div>Seleccione</div>}
											options={estates ? estates : []}
											onChange={(value) => {
												let newEstates = [];
												value &&
													value.map((estate) => {
														newEstates.push(estate.value);
														return null;
													});
												handleEstate(newEstates);
											}}
											components={{
												NoOptionsMessage,
											}}
											isDisabled={!isManager}
											styles={customStyles}
											isMulti={true}
										/>
									</Form.Group>
								</Col>
							</>
						)}
						<Col md={12} xs={12}>
							<hr style={{ borderTop: "1px solid #7fb02f" }} />
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Categoría</Form.Label>
								<Select
									value={categories && categories.filter((c) => Number(c.value) === Number(data.category_id))}
									placeholder={<div>Seleccione Paso 1</div>}
									options={categories ? categories.filter((c) => c.estate_ids && c.estate_ids.some((eid) => data.estates.includes(eid))) : []}
									onChange={(value) =>
										setData({
											...data,
											category_id: value ? value.value : "",
										})
									}
									components={{
										NoOptionsMessage,
									}}
									isDisabled={!isManager}
									styles={customStyles}
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Paso 1</Form.Label>
								<Select
									isMulti
									value={species && species.filter((s) => data.species && data.species.includes(s.value))}
									placeholder={<div>Seleccione Paso 1</div>}
									options={getSpecies(data.estates)}
									onChange={(value) => {
										let newSpecies = [];
										value &&
											value.map((specie) => {
												newSpecies.push(specie.value);
												return null;
											});
										setData({
											...data,
											species: newSpecies,
										});
									}}
									components={{
										NoOptionsMessage,
									}}
									isDisabled={!isManager}
									styles={customStyles}
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Paso 2</Form.Label>
								<Select
									isMulti
									value={varieties && varieties.filter((v) => data.varieties && data.varieties.includes(v.value))}
									placeholder={<div>Seleccione Paso 2</div>}
									options={data.species.length !== 0 ? varieties.filter((v) => data.species.includes(v.specie_id)) : []}
									onChange={(value) => {
										let newVarieties = [];
										value &&
											value.map((variety) => {
												newVarieties.push(variety.value);
												return null;
											});
										setData({
											...data,
											varieties: newVarieties,
										});
									}}
									components={{
										NoOptionsMessage,
									}}
									isDisabled={!isManager}
									styles={customStyles}
								/>
							</Form.Group>
						</Col>
						<Col md={6} xs={12}>
							<Form.Group className={classes.group_form}>
								<Form.Label className={classes.label}>Paso 3</Form.Label>
								<Select
									isMulti
									value={quarters && quarters.filter((q) => data.quarters && data.quarters.includes(q.value))}
									placeholder={<div>Seleccione Paso 3</div>}
									options={data.varieties.length !== 0 ? quarters.filter((q) => data.varieties.includes(q.variety_id)) : []}
									onChange={(value) => {
										let newQuarters = [];
										value &&
											value.map((quarter) => {
												newQuarters.push(quarter.value);
												return null;
											});
										setData({
											...data,
											quarters: newQuarters,
										});
									}}
									components={{
										NoOptionsMessage,
									}}
									isDisabled={!isManager}
									styles={customStyles}
								/>
							</Form.Group>
						</Col>
						<Col md={12} xs={12}>
							<hr style={{ borderTop: "1px solid #7fb02f" }} />
						</Col>
					</Row>
				)}
			</Modal.Body>
			<Modal.Footer>
				{(slug ? hasPermission("edit", permissions) : hasPermission("create", permissions)) && !isFetching && (
					<Button variant="success" onClick={handleSave}>
						GUARDAR
					</Button>
				)}
			</Modal.Footer>
		</Modal>
	);
};

export default BusinessCenterForm;
